import axios from "axios";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

function Stock() {
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("day-wise");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [stocktotal, setStockTotal] = useState({});
  const [stocktotalinventory, setStockTotalInventory] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedStockId, setSelectedStockId] = useState(null);
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
  };

  // Navigate to add stock pages
  const handleAddStock = () => {
    navigate("/add-stock");
  };

  const handleAddPersonalStock = () => {
    navigate("/personal-stock");
  };

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activeTab]);


  const fetchData = async () => {
    try {
      setLoading(true);
      let url;
      if (activeTab === "day-wise") {
        url = `${ApiUrl}/Api/stock.php`;
      } else {
        url = `${ApiUrl}/Api/stock.php`;
      }

      const response = await axios.get(url);
      console.log(`Response from ${activeTab}:`, response.data?.data);

      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setResponseData(data);

      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      toast.error("Failed to fetch data", 3000, "topRight");
      setResponseData([]);
      setLoading(false);
    }
  };


  // Fetch stock totals
  const fetchDatastock = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/stock.php/stock_total`);
      setStockTotal(response.data?.data || {});
      console.log("Stock total response:", response.data.data);
    } catch (error) {
      console.error("Error fetching stock totals:", error.message);
    }
  };

  // Fetch stock inventory totals
  const fetchDatastockinventory = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/stock.php/metal-totals`);
      setStockTotalInventory(response.data?.data || {});
      console.log("Stock inventory response:", response.data.data);
    } catch (error) {
      console.error("Error fetching stock inventory:", error.message);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/notification.php`);
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
      toast.error("Failed to fetch notifications", 3000, "topRight");
    }
  };

  // Check if a notification is accepted for the action
  const checkNotificationStatus = (stockId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === stockId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "accepted" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  // Check if a notification is pending
  const hasPendingNotification = (stockId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === stockId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "requested" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  // Open notification modal
  const openNotificationModal = (id, action) => {
    setSelectedStockId(id);
    setActionType(action);
    setShowNotificationModal(true);
  };

  // Close notification modal
  const closeNotificationModal = () => {
    setShowNotificationModal(false);
    setSelectedStockId(null);
    setActionType("");
    setIsSubmitting(false);
  };

  // Handle notification submission
  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;
    setIsSubmitting(true);

    if (hasPendingNotification(selectedStockId, actionType)) {
      toast.warning("A pending request for this action already exists!", 3000, "topRight");
      closeNotificationModal();
      return;
    }

    try {
      const userId = localStorage.getItem("username");
      const currentTime = new Date().toISOString().slice(0, 19).replace("T", " ");

      const formData = {
        table_name: "stock",
        table_id: selectedStockId,
        employe_id: userId || "",
        status: "requested",
        button_value: actionType,
        time: currentTime,
        viewed: "false",
        created_date: currentTime,
        updated_by: userId || "",
        updated_date: currentTime,
      };

      await axios.post(`${ApiUrl}/Api/notification.php`, formData, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(`${actionType} request sent successfully!`, 3000, "topRight");
      closeNotificationModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error submitting notification:", error.message);
      toast.error("Error submitting notification. Please try again.", 3000, "topRight");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch data and notifications periodically
  useEffect(() => {
    fetchData();
    fetchDatastock();
    fetchDatastockinventory();
    fetchNotifications();
    const interval = setInterval(() => {
      fetchData();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!Array.isArray(responseData)) return [];

    return responseData.filter((item) => {
      if (!item) return false;

      const searchMatch =
        searchTerm === "" ||
        (item.item &&
          item.item.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.category &&
          item.category.toLowerCase().includes(searchTerm.toLowerCase()));

      return searchMatch;
    });
  }, [responseData, searchTerm]);

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);

  // Get current items for pagination
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  // Export Functions
  const handlePrint = () => {
    let tableHtml = "";
    let title = "";

    if (activeTab === "day-wise") {
      title = "Day-Wise Stock Report";
      tableHtml = `
        <h2>${title}</h2>
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px;">S.No</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Category</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Item</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Weight</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Total Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Sold Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Available Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Stock IDs</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData
          .map(
            (item, index) => {
              const userId = localStorage.getItem("username");
              const notification = notifications.find(
                (notif) =>
                  notif.table_id === item.id?.toString() &&
                  notif.employe_id === userId &&
                  notif.viewed === "false"
              );
              let rowClass = "";
              if (notification) {
                switch (notification.status) {
                  case "accepted":
                    rowClass = "background-color: #e2e3e5;";
                    break;
                  case "rejected":
                    rowClass = "background-color: #f8d7da;";
                    break;
                  case "requested":
                    rowClass = "background-color: #cff4fc;";
                    break;
                }
              }
              return `
                    <tr style="${rowClass}">
                      <td style="border: 1px solid #ddd; padding: 8px;">${index + 1}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item?.category || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.item || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.weight || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.total_quantity || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.sold_quantity || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.avaliable_quantity || ""}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.stock_ids && item.stock_ids.length > 0
                  ? item.stock_ids.join(", ")
                  : "No IDs"
                }</td>
                    </tr>
                  `;
            }
          )
          .join("")}
          </tbody>
        </table>
      `;
    } else {
      title = "Stock Inventory Report";
      tableHtml = `
        <h2>${title}</h2>
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px;">S.No</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Category</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Type</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Weight</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Total</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Stock IDs</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData
          .map(
            (item, index) => {
              const userId = localStorage.getItem("username");
              const notification = notifications.find(
                (notif) =>
                  notif.table_id === item.id?.toString() &&
                  notif.employe_id === userId &&
                  notif.viewed === "false"
              );
              let rowClass = "";
              if (notification) {
                switch (notification.status) {
                  case "accepted":
                    rowClass = "background-color: #e2e3e5;";
                    break;
                  case "rejected":
                    rowClass = "background-color: #f8d7da;";
                    break;
                  case "requested":
                    rowClass = "background-color: #cff4fc;";
                    break;
                }
              }
              return `
                    <tr style="${rowClass}">
                      <td style="border: 1px solid #ddd; padding: 8px;">${index + 1}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.category || "N/A"}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.type || item.item || "N/A"}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.weight || "N/A"}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.total || item.total_quantity || "N/A"}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${item.stock_ids && item.stock_ids.length > 0
                  ? item.stock_ids.join(", ")
                  : "No IDs"
                }</td>
                    </tr>
                  `;
            }
          )
          .join("")}
          </tbody>
        </table>
      `;
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h2 { color: #333; text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            @media print {
              body { margin: 0; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          ${tableHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    toast.success("Print exported successfully!", 3000, "topRight");
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      const title = activeTab === "day-wise" ? "Day-Wise Stock Report" : "Stock Inventory Report";
      doc.text(title, 14, 22);
      doc.setFontSize(12);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 35);

      const userId = localStorage.getItem("username");

      // Attach rowStyle to filteredData items
      filteredData.forEach((item) => {
        const notification = notifications.find(
          (notif) =>
            notif.table_id === item.id?.toString() &&
            notif.employe_id === userId &&
            notif.viewed === "false"
        );
        if (notification) {
          switch (notification.status) {
            case "accepted":
              item.rowStyle = { fillColor: [226, 227, 229] };
              break;
            case "rejected":
              item.rowStyle = { fillColor: [248, 215, 218] };
              break;
            case "requested":
              item.rowStyle = { fillColor: [207, 244, 252] };
              break;
            default:
              item.rowStyle = {};
          }
        } else {
          item.rowStyle = {};
        }
      });

      if (activeTab === "day-wise") {
        autoTable(doc, {
          startY: 45,
          head: [
            [
              "S.No",
              "Date",
              "Category",
              "Item",
              "Total Qty",
              "Total Weight",
              "Sold Weight",
              "Available Weight",
              "Stock IDs",
            ],
          ],
          body: filteredData.map((item, index) => [
            index + 1,
            (item.stock_list && item.stock_list.length > 0 ? item.stock_list[0]?.date : "") || "",
            item.category || "",
            item.item || "",
            item.total_quantity || "",
            item.weight || "",
            item.sold_weight || "0.00g",
            item.avaliable_weight || item.weight || "",
            item.stock_ids && item.stock_ids.length > 0
              ? item.stock_ids.join(", ")
              : "No IDs",
          ]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [66, 139, 202], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 12 },
            1: { cellWidth: 18 },
            2: { cellWidth: 20 },
            3: { cellWidth: 20 },
            4: { cellWidth: 16 },
            5: { cellWidth: 18 },
            6: { cellWidth: 18 },
            7: { cellWidth: 20 },
            8: { cellWidth: 24 },
          },
          didParseCell: (data) => {
            if (data.section === "body") {
              const rowIndex = data.row.index;
              const rowStyle = filteredData[rowIndex].rowStyle;
              if (rowStyle) {
                data.cell.styles = { ...data.cell.styles, ...rowStyle };
              }
            }
          },
        });
        doc.save("day_wise_stock_report.pdf");
      } else {
        autoTable(doc, {
          startY: 45,
          head: [["S.No", "Category", "Type", "Weight", "Total", "Stock IDs"]],
          body: filteredData.map((item, index) => [
            index + 1,
            item.category || "N/A",
            item.type || item.item || "N/A",
            item.weight || "N/A",
            item.total || item.total_quantity || "N/A",
            item.stock_ids && item.stock_ids.length > 0
              ? item.stock_ids.join(", ")
              : "No IDs",
          ]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [66, 139, 202], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25 },
            3: { cellWidth: 20 },
            4: { cellWidth: 20 },
            5: { cellWidth: 30 },
          },
          didParseCell: (data) => {
            if (data.section === "body") {
              const rowIndex = data.row.index;
              const rowStyle = filteredData[rowIndex].rowStyle;
              if (rowStyle) {
                data.cell.styles = { ...data.cell.styles, ...rowStyle };
              }
            }
          },
        });
        doc.save("stock_inventory_report.pdf");
      }

      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF", 3000, "topRight");
    }
  };



  const exportCSV = () => {
    let headers;
    let rows;
    let filename;

    if (activeTab === "day-wise") {
      headers = [
        "S.No",
        "Date",
        "Category",
        "Item",
        "Total Quantity",
        "Total Weight",
        "Sold Weight",
        "Available Weight",
        "Stock IDs",
      ];
      rows = filteredData.map((item, index) => [
        index + 1,
        (item.stock_list && item.stock_list.length > 0 ? item.stock_list[0]?.date : "") || "",
        item?.category || "",
        item.item || "",
        item.total_quantity || "",
        item.weight || "",
        item.sold_weight || "0.00g",
        item.avaliable_weight || item.weight || "",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
      filename = "day_wise_stock_report.csv";
    } else {
      headers = ["S.No", "Category", "Type", "Weight", "Total", "Stock IDs"];
      rows = filteredData.map((item, index) => [
        index + 1,
        item.category || "N/A",
        item.type || item.item || "N/A",
        item.weight || "N/A",
        item.total || item.total_quantity || "N/A",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
      filename = "stock_inventory_report.csv";
    }

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((field) => `"${field}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported successfully!", 3000, "topRight");
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    let wsData;
    let filename;

    if (activeTab === "day-wise") {
      wsData = [
        [
          "S.No",
          "Date",
          "Category",
          "Item",
          "Total Quantity",
          "Total Weight",
          "Sold Weight",
          "Available Weight",
          "Stock IDs",
        ],
        ...filteredData.map((item, index) => [
          index + 1,
          (item.stock_list && item.stock_list.length > 0 ? item.stock_list[0]?.date : "") || "",
          item?.category || "",
          item.item || "",
          item.total_quantity || "",
          item.weight || "",
          item.sold_weight || "0.00g",
          item.avaliable_weight || item.weight || "",
          item.stock_ids && item.stock_ids.length > 0
            ? item.stock_ids.join("; ")
            : "No IDs",
        ]),
      ];
      filename = "day_wise_stock_report.xlsx";
    } else {
      wsData = [
        ["S.No", "Category", "Type", "Weight", "Total", "Stock IDs"],
        ...filteredData.map((item, index) => [
          index + 1,
          item.category || "N/A",
          item.type || item.item || "N/A",
          item.weight || "N/A",
          item.total || item.total_quantity || "N/A",
          item.stock_ids && item.stock_ids.length > 0
            ? item.stock_ids.join("; ")
            : "No IDs",
        ]),
      ];
      filename = "stock_inventory_report.xlsx";
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");

    const colWidths = wsData[0].map((_, colIndex) => {
      const maxLength = Math.max(
        ...wsData.map((row) =>
          row[colIndex] ? row[colIndex].toString().length : 0
        )
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, filename);
    toast.success("Excel exported successfully!", 3000, "topRight");
  };

  const copyToClipboard = () => {
    let headers;
    let rows;

    if (activeTab === "day-wise") {
      headers = [
        "S.No",
        "Date",
        "Category",
        "Item",
        "Total Quantity",
        "Total Weight",
        "Sold Weight",
        "Available Weight",
        "Stock IDs",
      ];
      rows = (filteredData || []).map((item, index) => [
        index + 1,
        (item.stock_list && item.stock_list.length > 0 ? item.stock_list[0]?.date : "") || "",
        item?.category || "",
        item.item || "",
        item.total_quantity || "",
        item.weight || "",
        item.sold_weight || "0.00g",
        item.avaliable_weight || item.weight || "",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
    } else {
      headers = ["S.No", "Category", "Type", "Weight", "Total", "Stock IDs"];
      rows = (filteredData || []).map((item, index) => [
        index + 1,
        item.category || "N/A",
        item.type || item.item || "N/A",
        item.weight || "N/A",
        item.total || item.total_quantity || "N/A",
        item.stock_ids && item.stock_ids.length > 0
          ? item.stock_ids.join("; ")
          : "No IDs",
      ]);
    }

    const formattedText = [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join("\t"))
      .join("\n");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(formattedText)
        .then(() => {
          toast.success("Table data copied to clipboard!", 3000, "topRight");
        })
        .catch((err) => {
          console.error("Clipboard API failed:", err);
          fallbackCopy(formattedText);
        });
    } else {
      fallbackCopy(formattedText);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = 0;
      textArea.style.left = 0;
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = 0;
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        toast.success("Table data copied to clipboard!", 3000, "topRight");
      } else {
        toast.error("Failed to copy data", 3000, "topRight");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  // Render pagination buttons
  const renderPageNumbers = () => {
    if (showAllItems || totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  const userRole = localStorage.getItem("role");

  const handleeditstock = (item) => {
    const stockId = item.id || item.stock_ids?.[0]; // Use item.id or first stock_id
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(stockId, "Edit")) {
        navigate(`/Viewstock/${item.stock_ids.join(",")}`);
      } else {
        openNotificationModal(stockId, "Edit");
      }
    } else {
      navigate(`/Viewstock/${item.stock_ids.join(",")}`);
    }
  };



  const handleEdit = (item) => {
    const stockId = item.id || item.stock_ids?.[0]; // Use item.id or first stock_id

    // Create stock_ids string for the route
    const stockIdsString = Array.isArray(item.stock_ids)
      ? item.stock_ids.join(",")
      : item.stock_ids;

    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(stockId, "Edit")) {
        navigate(`/Edit-stock/${stockIdsString}`);
      } else {
        openNotificationModal(stockId, "Edit");
      }
    } else {
      navigate(`/Edit-stock/${stockIdsString}`);
    }
  };


  const handleDelete = (item) => {
    const stockId = item.id || item.stock_ids?.[0]; // Use item.id or first stock_id
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(stockId, "Delete")) {
        setItemToDelete(item);
        setShowDeleteModal(true);
      } else {
        openNotificationModal(stockId, "Delete");
      }
    } else {
      setItemToDelete(item);
      setShowDeleteModal(true);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setDeleteLoading(true);

      const stockIdsToDelete = itemToDelete.stock_ids || [];
      if (stockIdsToDelete.length === 0) {
        throw new Error("No stock IDs found to delete");
      }

      const deleteData = {
        stock_ids: stockIdsToDelete,
        item_name: itemToDelete.item,
        category: itemToDelete.category,
      };

      const response = await axios.delete(`${ApiUrl}/Api/stock.php`, {
        data: deleteData,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.status === true || response.data.success === true) {
        // Update notification if it exists
        const stockId = itemToDelete.id || itemToDelete.stock_ids?.[0];
        const notification = notifications.find(
          (notif) =>
            notif.table_id === stockId.toString() &&
            notif.button_value === "Delete" &&
            notif.viewed === "false"
        );
        if (notification) {
          const currentTime = new Date().toISOString().slice(0, 19).replace("T", " ");
          const notificationPayload = {
            id: notification.id,
            viewed: "true",
            updated_by: localStorage.getItem("username"),
            updated_date: currentTime,
            table_name: notification.table_name,
            table_id: notification.table_id,
            employe_id: notification.employe_id,
            status: notification.status,
            button_value: notification.button_value,
            time: notification.time,
            created_date: notification.created_date,
          };

          await axios.put(
            `${ApiUrl}/Api/notification.php/${notification.id}`,
            notificationPayload,
            {
              headers: { "Content-Type": "application/json" },
            }
          );
        }

        setResponseData((prevData) =>
          prevData.filter((item) => {
            if (!item.stock_ids || !Array.isArray(item.stock_ids)) return true;
            const hasOverlap = item.stock_ids.some((stockId) =>
              stockIdsToDelete.includes(stockId)
            );
            return !hasOverlap;
          })
        );

        toast.success(
          `Stock item "${itemToDelete.item}" deleted successfully`,
          3000,
          "topRight"
        );

        closeDeleteModal();

        const remainingItems = responseData.filter((item) => {
          if (!item.stock_ids || !Array.isArray(item.stock_ids)) return true;
          const hasOverlap = item.stock_ids.some((stockId) =>
            stockIdsToDelete.includes(stockId)
          );
          return !hasOverlap;
        });

        if (!showAllItems) {
          const newTotalPages = Math.ceil(remainingItems.length / itemsPerPage);
          if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
          }
        }

        if (response.data.warnings && response.data.warnings.length > 0) {
          console.warn("Delete warnings:", response.data.warnings);
          toast.warning(
            `Some issues occurred: ${response.data.warnings.join(", ")}`,
            5000,
            "topRight"
          );
        }

        fetchNotifications();
      } else {
        throw new Error(response.data.message || "Delete operation failed");
      }
    } catch (error) {
      console.error("Error deleting stock item:", error);
      let errorMessage = "Failed to delete stock item";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, 5000, "topRight");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete Modal Component
  const DeleteModal = () => {
    if (!showDeleteModal || !itemToDelete) return null;

    return (
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Confirm Delete Stock Item
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning d-flex align-items-center mb-4">
                <i
                  className="fas fa-exclamation-triangle me-3"
                  style={{ fontSize: "2rem" }}
                ></i>
                <div>
                  <h6 className="mb-1">
                    Warning: This action cannot be undone!
                  </h6>
                  <p className="mb-0">
                    You are about to permanently delete this stock item and all
                    its associated records.
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="card border-primary h-100">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Item Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      <table className="table table-borderless table-sm">
                        <tbody>
                          <tr>
                            <td><strong>Item Name:</strong></td>
                            <td>{itemToDelete.item}</td>
                          </tr>
                          <tr>
                            <td><strong>Category:</strong></td>
                            <td>
                              <span
                                className={`badge ${itemToDelete.category === "Gold"
                                  ? "bg-warning text-dark"
                                  : "bg-secondary"
                                  }`}
                              >
                                {itemToDelete.category}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Weight:</strong></td>
                            <td>{itemToDelete.weight}</td>
                          </tr>
                          <tr>
                            <td><strong>Total Quantity:</strong></td>
                            <td>
                              <span className="badge bg-info">
                                {itemToDelete.total_quantity}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Sold Quantity:</strong></td>
                            <td>
                              <span className="badge bg-success">
                                {itemToDelete.sold_quantity}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Available Quantity:</strong></td>
                            <td>
                              <span className="badge bg-primary">
                                {itemToDelete.avaliable_quantity}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card border-warning h-100">
                    <div className="card-header bg-warning text-dark">
                      <h6 className="mb-0">
                        <i className="fas fa-database me-2"></i>
                        Records to Delete
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <strong>Stock IDs:</strong>
                        <div className="mt-2">
                          {itemToDelete.stock_ids &&
                            itemToDelete.stock_ids.map((stockId, index) => (
                              <span
                                key={index}
                                className="badge bg-danger me-1 mb-1"
                              >
                                ID: {stockId}
                              </span>
                            ))}
                        </div>
                      </div>

                      {itemToDelete.stock_list &&
                        itemToDelete.stock_list.length > 0 && (
                          <>
                            <strong>
                              Stock Records ({itemToDelete.stock_list.length}{" "}
                              total):
                            </strong>
                            <div
                              className="mt-2"
                              style={{ maxHeight: "200px", overflowY: "auto" }}
                            >
                              {itemToDelete.stock_list.map((stock, index) => (
                                <div
                                  key={index}
                                  className="border rounded p-2 mb-2 bg-light"
                                >
                                  <div className="row">
                                    <div className="col-6">
                                      <small>
                                        <strong>Record ID:</strong> {stock.id}
                                        <br />
                                        <strong>Date:</strong> {stock.date}
                                        <br />
                                        <strong>Stock ID:</strong>{" "}
                                        {stock.overall_stock_id}
                                      </small>
                                    </div>
                                    <div className="col-6">
                                      <small>
                                        <strong>Quantity:</strong>{" "}
                                        {stock.quantity}
                                        <br />
                                        <strong>Status:</strong>
                                        <span
                                          className={`badge ms-1 ${stock.status === "in"
                                            ? "bg-success"
                                            : "bg-warning text-dark"
                                            }`}
                                        >
                                          {stock.status}
                                        </span>
                                        <br />
                                        <strong>Available:</strong>{" "}
                                        {stock.available_quantity}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="alert alert-danger mt-3 mb-0">
                <strong>Impact:</strong> Deleting this item will remove{" "}
                <strong>{itemToDelete.stock_list?.length || 0}</strong> database
                records and make{" "}
                <strong>{itemToDelete.avaliable_quantity}</strong> units
                unavailable.
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                <i className="fas fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash me-2"></i>
                    Permanently Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // // Notification Modal
  // Ascending
  const NotificationModal = () => (
    <div
      className={`modal fade ${showNotificationModal ? "show" : ""}`}
      style={{ display: showNotificationModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      aria-labelledby="notificationModalLabel"
      aria-hidden={!showNotificationModal}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title text-primary" id="notificationModalLabel">
              Send {actionType} Request to Admin
            </h6>
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: "10px" }}
              onClick={closeNotificationModal}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleNotificationSubmit}>
              <p className="text-center">
                Do you want to request to {actionType.toLowerCase()} this stock item?
              </p>
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-4 me-2"
                  onClick={closeNotificationModal}
                >
                  No
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm px-4"
                  disabled={isSubmitting}
                >
                  Yes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-3">
      {/* {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )} */}

      <>
        <div className="row mt-2">
          <div className="col-lg-2">
            <button
              className="btn btn-primary btn-sm rounded-3"
              style={{ marginTop: "-30px" }}
              onClick={handleAddStock}
            >
              Add Stock <i className="fa-solid fa-plus mx-1"></i>
            </button>
          </div>
          <div className="col-lg-8"></div>
          <div className="col-lg-2">
            {userRole === "Admin" && (
              <button
                className="btn btn-success btn-sm rounded-3"
                style={{ marginTop: "-30px" }}
                onClick={handleAddPersonalStock}
              >
                Personal Stock <i className="fa-solid fa-plus mx-1"></i>
              </button>
            )}
          </div>
        </div>
        <div className="">
          {/* Tabs */}
          <div className="card shadow-sm border-0 rounded-3 mb-2">
            <div className="row p-2">
              <div className="col-auto">
                <button
                  className={`btn btn-link text-decoration-none px-3 ${activeTab === "day-wise"
                    ? "text-white border-bottom bg-primary"
                    : "text-dark"
                    }`}
                  onClick={() => handleTabClick("day-wise")}
                >
                  <span style={{ fontSize: "13px" }}>Day-Wise</span>
                </button>
                <button
                  className={`btn btn-link text-decoration-none px-3 ms-3 ${activeTab === "stock-inventory"
                    ? "text-white border-bottom bg-primary"
                    : "text-dark"
                    }`}
                  onClick={() => handleTabClick("stock-inventory")}
                >
                  <span style={{ fontSize: "13px" }}>Stock Inventory</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {activeTab === "day-wise" && (
            <>
              <div className="row mb-2">
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Buy Qty</h6>
                    <p className="fw-bold">{stocktotal.total_buy_qty || 0}</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Sales Qty</h6>
                    <p className="fw-bold">{stocktotal.total_sales_qty || 0}</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Item Sold Today</h6>
                    <p className="fw-bold">{stocktotal.items_sold_today || 0}</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Available Qty</h6>
                    <p className="fw-bold">{stocktotal.available_qty || 0}</p>
                  </div>
                </div>
              </div>
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center mb-2 mb-md-0">
                      <label className="form-label text-dark mb-0 me-2">
                        Show:{" "}
                      </label>
                      <div>
                        <select
                          className="form-control"
                          onChange={handleItemsPerPageChange}
                          value={showAllItems ? "All" : itemsPerPage}
                        >
                          <option value="All">All</option>
                          {[5, 10, 20, 100, 200, 500, 1000].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mb-2 mb-md-0 flex-wrap">
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={handlePrint}
                      >
                        Print
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportPDF}
                      >
                        PDF
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={copyToClipboard}
                      >
                        Copy
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportCSV}
                      >
                        CSV
                      </button>
                      <button
                        className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0"
                        onClick={exportExcel}
                      >
                        Excel
                      </button>
                    </div>

                    <div className="search-container w-25">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          aria-label="Search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
           
                  {/* Table */}
                  <div className="card tableCard p-2">

                    <div className="table-responsive">
                      <table className="table table-hover w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>S.No</th>
                            <th>Date</th>
                            <th>Category</th>
                            <th>Item</th>
                            <th>Total Quantity</th>
                            <th>Total Weight</th>
                            <th>Sold Weight</th>
                            <th>Available Weight</th>

                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length > 0 ? (
                            currentItems.map((item, index) => {
                              const userId = localStorage.getItem("username");
                              const notification = notifications.find(
                                (notif) =>
                                  notif.table_id === (item.id || item.stock_ids?.[0])?.toString() &&
                                  notif.employe_id === userId &&
                                  notif.viewed === "false"
                              );
                              let rowClass = "";
                              if (notification) {
                                switch (notification.status) {
                                  case "accepted":
                                    rowClass = "table-secondary";
                                    break;
                                  case "rejected":
                                    rowClass = "table-danger";
                                    break;
                                  case "requested":
                                    rowClass = "table-info";
                                    break;
                                }
                              }
                              return (
                                <tr key={item.id || `inventory-${index}`} className={rowClass}>
                                  <td>
                                    {showAllItems
                                      ? index + 1
                                      : (currentPage - 1) * itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td>
  {item?.stock_list && Array.isArray(item.stock_list) && item.stock_list.length > 0 
    ? (item.stock_list[0]?.date || "N/A")
    : "N/A"}
</td>
                                  <td>{item?.category}</td>
                                  <td>{item.item}</td>
                                  <td>{item.total_quantity}</td>
                                  <td>{item.weight}</td>
                                  <td>{item.sold_weight ?? "0.00g"}</td>
                                  <td>{item.avaliable_weight ?? item.weight}</td>

                                  <td>
                                    <button
                                      className="btn btn-light btn-sm mx-1"
                                      onClick={() => handleEdit(item)}
                                      title="Edit"
                                    >

                                      <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                                    </button>
                                    <button
                                      className="btn btn-light btn-sm"
                                      onClick={() => handleDelete(item)}
                                      title="Delete"
                                    >
                                      <i className="fa-solid fa-trash text-danger fs-6"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center">
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {!showAllItems && totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <ul className="pagination justify-content-end mt-3">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "stock-inventory" && (
            <>
              <div className="row mb-2">
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Gold Stock</h6>
                    <p className="fw-bold text-warning">{stocktotalinventory.Total_Gold_Stock || 0}</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Silver Stock</h6>
                    <p className="fw-bold text-secondary">{stocktotalinventory.Total_Silver_Stock || 0}</p>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card p-2">
                    <h6>Total Items</h6>
                    <p className="fw-bold">{stocktotalinventory.Total_Items || 0}</p>
                  </div>
                </div>
              </div>
              <div
                className="card shadow-sm border-0 rounded-3"
                style={{ background: "#ffffff", opacity: 0.9 }}
              >
                <div className="card-body">
                  <div className="row align-items-center mb-4">
                    <div className="col-md-9">
                      <h6 className="mb-0">Stock Inventory</h6>
                    </div>
                    <div className="col-md-3">
                      <div className="position-relative">
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                          type="search"
                          className="form-control ps-5"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card tableCard p-2">
                    <div className="table-responsive">
                      <table className="table table-hover w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>Item</th>
                            <th>Total Quantity</th>
                            <th>Total Weight</th>
                            <th>Sold Weight</th>
                            <th>Available Weight</th>

                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length > 0 ? (
                            currentItems.map((item, index) => {
                              const userId = localStorage.getItem("username");
                              const notification = notifications.find(
                                (notif) =>
                                  notif.table_id === (item.id || item.stock_ids?.[0])?.toString() &&

                                  notif.viewed === "false"
                              );
                              let rowClass = "";
                              if (notification) {
                                switch (notification.status) {
                                  case "accepted":
                                    rowClass = "table-secondary";
                                    break;
                                  case "rejected":
                                    rowClass = "table-danger";
                                    break;
                                  case "requested":
                                    rowClass = "table-info";
                                    break;
                                }
                              }
                              return (
                                <tr key={item.id || `inventory-${index}`} className={rowClass}>
                                  <td>
                                    {showAllItems
                                      ? index + 1
                                      : (currentPage - 1) * itemsPerPage +
                                      index +
                                      1}
                                  </td>
                                  <td>{item.category || "N/A"}</td>
                                  <td>
                                    {item.item_name_english
                                      ? `${item.item_name_english}${item.item_name_tamil ? " / " + item.item_name_tamil : ""}`
                                      : (item.item || item.type || "N/A")}
                                  </td>
                                  <td>
                                    {item.total_quantity || "N/A"}
                                  </td>
                                  <td>{item.weight || "N/A"}</td>
                                  <td>{item.sold_weight ?? "0.00g"}</td>
                                  <td>{item.avaliable_weight ?? item.weight}</td>

                                  <td>
                                    <button
                                      className="btn btn-light btn-sm mx-1"
                                      onClick={() => handleeditstock(item)}
                                      title="Edit"
                                    >

                                      <i className="fa-solid fa-list text-primary fs-6"></i>
                                    </button>
                                    <button
                                      className="btn btn-light btn-sm"
                                      onClick={() => handleDelete(item)}
                                      title="Delete"
                                    >
                                      <i className="fa-solid fa-trash text-danger fs-6"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {!showAllItems && totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <ul className="pagination justify-content-end mt-3">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </>

      {/* Modals */}
      <DeleteModal />
      <NotificationModal />
      {(showDeleteModal || showNotificationModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default Stock;